// ExternalError — the shell's boundary-failure vocabulary. ONE typed
// sum the views can branch on and `toDisplay` can render; nothing raw
// (an exception string, a provider error body) ever crosses this
// boundary into a user surface. Pure: no I/O here. The fetch-side
// mapping (HTTP status -> which variant) lives in `httpClient` (infra);
// this file owns the type, its constructors, and the pure total
// `toDisplay`.
//
// Domain refusals ride the `Refused` variant: the wire's `ok:false`
// envelope is no longer flattened to `Rejected` — `httpClient` reads
// the refusing leaf's discriminant, `errorClass.ts` classifies it into
// a failure family, and `toDisplay` picks default-by-class copy with a
// per-leaf override table (.claude/analysis/error-handling.md §3–§4). A
// `stale_content_hash`, a `resource_not_found` and a `title_empty` now
// render three different truths with three different recoveries.
//
// `detail` is for logs/telemetry ONLY and is never rendered — it
// carries the raw provider/exception text so a developer can diagnose
// without the user ever seeing dev-jargon. See the external-error
// model (docs/model/foundation/external-errors.md).
//
// `retriable` is DERIVED from the recovery `toDisplay` picks, not
// stored — one source of truth. `toDisplay` returns Lingui
// MessageDescriptors, not localized strings: `msg` is a macro that
// produces pure `{ id, message }` data, so this stays a pure, total
// function with no i18n instance. The shell localizes with
// `i18n._(display.title)` at render.

import * as Core from "@bilda/core";
import { exhaustSwitchCase } from "@bilda/utils";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import type { ErrorClass } from "./errorClass.js";
import { UNSUPPORTED_FILE_TYPE_MESSAGE } from "./ingest.js";

// Network layer never delivered a response (fetch threw / offline).
export type Unreachable = {
    readonly _tag: "External.Unreachable";
    readonly detail?: string;
};
// 401 — the session is gone; the recovery is to re-authenticate.
export type Unauthorized = { readonly _tag: "External.Unauthorized" };
// 403 — authenticated but not allowed; no client-side recovery.
export type Forbidden = { readonly _tag: "External.Forbidden" };
// 403 privacy_terms_not_accepted — signed in, but the person has not
// accepted the CURRENT privacy terms (first visit, or after a terms bump).
// The recovery is the consent gate, which the shell shows on this error.
export type PrivacyTermsNotAccepted = {
    readonly _tag: "External.PrivacyTermsNotAccepted";
};
// 429 — too many requests; back off and retry.
export type RateLimited = { readonly _tag: "External.RateLimited" };
// The account behind the agent's (valid) key is out of credits / over
// its billing cap (agent `quota_exhausted`, HTTP 402). Not auth (the
// key works) and not transient (retry won't help): the recovery is
// out-of-band — add credits. The billing-400 incident's actual kind.
export type QuotaExhausted = {
    readonly _tag: "External.QuotaExhausted";
};
// The organisation's own AI usage limit is reached (agent
// `usage_limit_reached`). Not out of credits: it lifts by itself.
// `resetsOn` is the calendar date (YYYY-MM-DD) it lifts, when known.
export type UsageLimitReached = {
    readonly _tag: "External.UsageLimitReached";
    readonly resetsOn?: string;
};
// Our own daily AI cost ceiling is reached (QA-14, agent
// `daily_ceiling_reached`): which one, its limit, when it resets.
export type DailyCeilingReached = {
    readonly _tag: "External.DailyCeilingReached";
    readonly reached: Core.AgentCall.DailyCeiling.CeilingReached;
};
// 400 — a protocol-level refusal (bad JSON, contract parse); the user
// must change the input. Domain refusals land as `Refused`, not here.
export type Rejected = {
    readonly _tag: "External.Rejected";
    readonly detail?: string;
};
// A typed domain/workflow refusal off the `ok:false` envelope.
// `errorType` is the refusing leaf's wire discriminant (telemetry +
// per-leaf display override); `errorClass` is its failure family and
// picks the default display.
export type Refused = {
    readonly _tag: "External.Refused";
    readonly errorType: string;
    readonly errorClass: ErrorClass;
    readonly detail?: string;
    // The titles of the parts the refusal names (a package's empty or
    // unfinished parts), for the display to list. Empty when none.
    readonly parts: readonly string[];
    // The element's text as it is now, when the refusal says it changed
    // since the writer read it (`element_changed_since_read`).
    readonly current?: string;
};
// 5xx — the server (or an upstream it depends on) failed; retry.
export type Upstream = {
    readonly _tag: "External.Upstream";
    readonly detail?: string;
};
// 2xx but the body wasn't the expected JSON envelope.
export type Malformed = {
    readonly _tag: "External.Malformed";
    readonly detail?: string;
};
// The agent has no (valid) API key on the server — the turn reached the
// server but no reply can ever come until the author supplies a key
// (BYOK: the desktop beta reads it at boot). Distinct from Unauthorized:
// the author's SESSION is fine; it's the agent's credential that's absent.
export type AgentAuthMissing = {
    readonly _tag: "External.AgentAuthMissing";
};
// The agent's provider throttled us or is over capacity — nothing is
// wrong with the connection or the request; wait a minute and retry.
export type AgentBusy = { readonly _tag: "External.AgentBusy" };
// The server couldn't reach the agent's provider (DNS, socket, timeout).
// Not `Unreachable`: the author's own connection to the app is fine.
export type AgentUnreachable = {
    readonly _tag: "External.AgentUnreachable";
    readonly detail?: string;
};
// Any other agent failure (provider rejected the request, unreadable
// reply, …). `detail` is the coarse category, telemetry only.
export type AgentFailed = {
    readonly _tag: "External.AgentFailed";
    readonly detail?: string;
};
// Catch-all — never leaks raw; carries detail for telemetry.
export type Unknown = {
    readonly _tag: "External.Unknown";
    readonly detail?: string;
};

export type ExternalError =
    | Unreachable
    | Unauthorized
    | Forbidden
    | PrivacyTermsNotAccepted
    | RateLimited
    | QuotaExhausted
    | UsageLimitReached
    | DailyCeilingReached
    | Rejected
    | Refused
    | Upstream
    | Malformed
    | AgentAuthMissing
    | AgentBusy
    | AgentUnreachable
    | AgentFailed
    | Unknown;

export const mkUnreachable = (detail?: string): Unreachable => ({
    _tag: "External.Unreachable",
    detail,
});
export const mkUnauthorized = (): Unauthorized => ({
    _tag: "External.Unauthorized",
});
export const mkForbidden = (): Forbidden => ({ _tag: "External.Forbidden" });
export const mkPrivacyTermsNotAccepted = (): PrivacyTermsNotAccepted => ({
    _tag: "External.PrivacyTermsNotAccepted",
});
export const mkRateLimited = (): RateLimited => ({
    _tag: "External.RateLimited",
});
export const mkQuotaExhausted = (): QuotaExhausted => ({
    _tag: "External.QuotaExhausted",
});
// Parse at the boundary: only a real calendar date (YYYY-MM-DD) is kept as
// the reset date; any other wire value means "not known".
export const mkUsageLimitReached = (
    rawResetsOn: unknown,
): UsageLimitReached => {
    const resetsOn = calendarDateOf(rawResetsOn);
    return resetsOn === undefined
        ? { _tag: "External.UsageLimitReached" }
        : { _tag: "External.UsageLimitReached", resetsOn };
};
// Parse at the boundary: a detail that is not a well-formed refusal still
// reads as a usage limit, never as a generic failure.
export const mkDailyCeilingReached = (
    rawDetail: unknown,
): DailyCeilingReached | UsageLimitReached => {
    const reached = Core.AgentCall.DailyCeiling.parseReachedDetail(rawDetail);
    return reached === undefined
        ? { _tag: "External.UsageLimitReached" }
        : { _tag: "External.DailyCeilingReached", reached };
};
const calendarDateOf = (raw: unknown): string | undefined => {
    if (typeof raw !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(raw))
        return undefined;
    const date = new Date(`${raw}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) &&
        date.toISOString().slice(0, 10) === raw
        ? raw
        : undefined;
};
export const mkRejected = (detail?: string): Rejected => ({
    _tag: "External.Rejected",
    detail,
});
export const mkRefused = (
    errorType: string,
    errorClass: ErrorClass,
    detail?: string,
    parts: readonly string[] = [],
    current?: string,
): Refused => ({
    _tag: "External.Refused",
    errorType,
    errorClass,
    detail,
    parts,
    ...(current === undefined ? {} : { current }),
});
export const mkUpstream = (detail?: string): Upstream => ({
    _tag: "External.Upstream",
    detail,
});
export const mkMalformed = (detail?: string): Malformed => ({
    _tag: "External.Malformed",
    detail,
});
export const mkAgentAuthMissing = (): AgentAuthMissing => ({
    _tag: "External.AgentAuthMissing",
});
export const mkAgentBusy = (): AgentBusy => ({ _tag: "External.AgentBusy" });
export const mkAgentUnreachable = (detail?: string): AgentUnreachable => ({
    _tag: "External.AgentUnreachable",
    detail,
});
export const mkAgentFailed = (detail?: string): AgentFailed => ({
    _tag: "External.AgentFailed",
    detail,
});
export const mkUnknown = (detail?: string): Unknown => ({
    _tag: "External.Unknown",
    detail,
});

// The ONE next action a surface offers after a failure (never a menu
// of options — alarm discipline). `Retry` transient; `Reload` stale
// state; `ReAuth` session lapsed; `GoBack` the target is gone;
// `FixInput` the input must change; `FixConfig` out-of-band (add
// credits, set a key); `None` nothing client-side helps.
export type Recovery =
    | "Retry"
    | "Reload"
    | "ReAuth"
    | "GoBack"
    | "FixInput"
    | "FixConfig"
    | "None";

// What a surface needs to render — human words, no jargon, no
// discriminants. `detail` never crosses into a Display.
export type Display = {
    readonly title: MessageDescriptor;
    readonly body?: MessageDescriptor;
    readonly severity: "info" | "warning" | "danger";
    readonly recovery: Recovery;
    readonly retriable: boolean;
};

// `retriable` is derived from the recovery — one source of truth.
const mkDisplay = (
    title: MessageDescriptor,
    severity: Display["severity"],
    recovery: Recovery,
    body?: MessageDescriptor,
): Display => ({
    title,
    body,
    severity,
    recovery,
    retriable: recovery === "Retry",
});

// A daily ceiling in plain words: whose, how much, when it resets (the
// reset as an absolute UTC moment, so a saved notice never goes stale).
// Shared by the toast, the thread notice and the interruption.
export const dailyCeilingCopy = (
    r: Core.AgentCall.DailyCeiling.CeilingReached,
): {
    readonly title: MessageDescriptor;
    // The title as a sentence, for the toast (title and body run on).
    readonly sentence: MessageDescriptor;
    readonly body: MessageDescriptor;
} => {
    const usd = r.limitMicroUsd / 1_000_000;
    const limit = Number.isInteger(usd) ? `${usd}` : usd.toFixed(2);
    const at = new Date(r.resetsAt).toISOString();
    const day = at.slice(0, 10);
    const time = at.slice(11, 16);
    const body = msg`The limit is USD ${limit} a day. It resets at ${time} UTC on ${day}.`;
    switch (r.scope) {
        case "account":
            return {
                title: msg`Your daily AI limit is reached`,
                sentence: msg`Your daily AI limit is reached.`,
                body,
            };
        case "deployment":
            return {
                title: msg`Today's AI limit for everyone is reached`,
                sentence: msg`Today's AI limit for everyone is reached.`,
                body,
            };
        default:
            return exhaustSwitchCase(r.scope);
    }
};

// When the usage limit lifts, if the vendor said. Shared with the thread
// notice so both read the same.
export const usageLimitResets = (
    resetsOn: string | undefined,
): MessageDescriptor | undefined =>
    resetsOn === undefined ? undefined : msg`It resets on ${resetsOn}.`;

// Default display per failure family — the canonical handling a BC
// leaf inherits by classification alone.
const classDisplay = (c: ErrorClass): Display => {
    switch (c) {
        case "not_found":
            return mkDisplay(
                msg`That can't be found.`,
                "warning",
                "GoBack",
                msg`It may have been deleted or moved.`,
            );
        case "already_exists":
            return mkDisplay(
                msg`One with that name already exists.`,
                "warning",
                "FixInput",
            );
        case "gone":
            return mkDisplay(msg`That has been deleted.`, "warning", "GoBack");
        case "not_deleted":
            return mkDisplay(
                msg`Nothing to restore — it isn't deleted.`,
                "info",
                "None",
            );
        case "invalid_input":
            return mkDisplay(
                msg`That value can't be accepted.`,
                "warning",
                "FixInput",
                msg`Check the input and try again.`,
            );
        case "conflict":
            return mkDisplay(
                msg`This changed somewhere else while you were working.`,
                "warning",
                "Reload",
                msg`Reload to pick up the latest version, then try again.`,
            );
        case "in_use":
            return mkDisplay(
                msg`It's still in use.`,
                "warning",
                "None",
                msg`Remove what depends on it first.`,
            );
        case "not_permitted":
            return mkDisplay(msg`That can't be changed.`, "warning", "None");
        case "precondition":
            return mkDisplay(
                msg`That can't be done right now.`,
                "warning",
                "Reload",
                msg`Things moved on since this view loaded — reload and try again.`,
            );
        case "specific":
            return mkDisplay(
                msg`The server couldn't complete that.`,
                "danger",
                "None",
            );
        default:
            return exhaustSwitchCase(c);
    }
};

// Per-leaf overrides — where a leaf's handling differs from its
// family's default. Keys are wire discriminants (domain or workflow).
// Every leaf classified `specific` MUST appear here — the `specific`
// class default is a last-resort fallback, not a display.
const leafDisplay: Readonly<Record<string, Display>> = {
    export_immutable: mkDisplay(
        msg`Exports are frozen snapshots.`,
        "info",
        "None",
        msg`Export the material again to get an updated package.`,
    ),
    content_unparseable: mkDisplay(
        msg`This content couldn't be read.`,
        "danger",
        "None",
    ),
    adapter_missing: mkDisplay(
        msg`That export format isn't available.`,
        "warning",
        "None",
        msg`Choose the Torus format instead.`,
    ),
    package_material_packaging_not_available: mkDisplay(
        msg`This material can't be exported in that format.`,
        "warning",
        "None",
        msg`Choose the Torus format instead.`,
    ),
    package_material_material_not_found: mkDisplay(
        msg`The material to export no longer exists.`,
        "warning",
        "None",
        msg`Open the material from Created materials and export again.`,
    ),
    package_material_material_not_authored: mkDisplay(
        msg`Only written materials can be exported.`,
        "warning",
        "None",
        msg`Open a material, not a source, and export again.`,
    ),
    package_material_parts_unfinished: mkDisplay(
        msg`Some parts aren't written yet.`,
        "warning",
        "None",
        msg`Generate them, then package again.`,
    ),
    package_material_parts_empty: mkDisplay(
        msg`Some parts are empty.`,
        "warning",
        "None",
        msg`Fill them or take them out, then export again.`,
    ),
    package_material_format_not_offered: mkDisplay(
        msg`That export format isn't offered.`,
        "warning",
        "None",
        msg`Choose Torus or Common Cartridge instead.`,
    ),
    package_material_render_failed: mkDisplay(
        msg`The learning environment's format can't hold this material as it is.`,
        "warning",
        "None",
        msg`Check that every part has content and every question a correct answer, then export again.`,
    ),
    adapter_failed: mkDisplay(
        msg`The export failed — try again.`,
        "danger",
        "Retry",
    ),
    content_hash_already_exists: mkDisplay(
        msg`A source with this exact content already exists.`,
        "warning",
        "FixInput",
    ),
    // `add_source_refused_duplicate_content` is retired (DB4, restoring
    // IDD `82fd6b99`) — see the NOTE in `errorClass.ts`'s
    // `workflowClassByType`. The FILE-path duplicate below is unaffected.
    add_file_refused_duplicate_content: mkDisplay(
        msg`A file with this exact content already exists.`,
        "warning",
        "FixInput",
    ),
    // The Studio's module commands: a card already in the module, and a
    // part that would put a module inside itself.
    module_parts_empty: mkDisplay(
        msg`That card is already in the module.`,
        "info",
        "None",
    ),
    module_parts_not_a_part: mkDisplay(
        msg`That card is no longer in the module.`,
        "info",
        "None",
    ),
    include_cycle: mkDisplay(
        msg`A module can't contain itself.`,
        "warning",
        "None",
        msg`That would put the module inside one of its own parts.`,
    ),
    // The server's demonstrator ingest gate (DemoScope, FR-3.2): same words
    // as the client's own pre-upload refusal.
    add_source_kind_not_accepted: mkDisplay(
        UNSUPPORTED_FILE_TYPE_MESSAGE,
        "warning",
        "FixInput",
    ),
};

// Pure, total. The ONLY thing a view calls with an ExternalError.
export const toDisplay = (e: ExternalError): Display => {
    switch (e._tag) {
        case "External.Unreachable":
            return mkDisplay(
                msg`Can't reach the server — check your connection.`,
                "danger",
                "Retry",
            );
        case "External.Unauthorized":
            return mkDisplay(
                msg`Your session expired — sign in again.`,
                "warning",
                "ReAuth",
            );
        case "External.Forbidden":
            return mkDisplay(
                msg`You don't have access to do that.`,
                "warning",
                "None",
            );
        case "External.PrivacyTermsNotAccepted":
            return mkDisplay(
                msg`Accept the updated privacy terms to continue.`,
                "warning",
                "Reload",
            );
        case "External.RateLimited":
            return mkDisplay(
                msg`Too many requests — wait a moment and try again.`,
                "warning",
                "Retry",
            );
        case "External.QuotaExhausted":
            return mkDisplay(
                msg`The assistant's account is out of credits.`,
                "danger",
                "FixConfig",
                msg`Ask your administrator to add credits, then try again.`,
            );
        case "External.UsageLimitReached":
            return mkDisplay(
                msg`The AI usage limit is reached.`,
                "warning",
                "None",
                usageLimitResets(e.resetsOn),
            );
        case "External.DailyCeilingReached": {
            const copy = dailyCeilingCopy(e.reached);
            return mkDisplay(copy.sentence, "warning", "None", copy.body);
        }
        case "External.Rejected":
            return mkDisplay(
                msg`The server couldn't accept that — check your input.`,
                "warning",
                "FixInput",
            );
        case "External.Refused":
            return leafDisplay[e.errorType] ?? classDisplay(e.errorClass);
        case "External.Upstream":
            return mkDisplay(
                msg`Something went wrong on the server — try again.`,
                "danger",
                "Retry",
            );
        case "External.Malformed":
            return mkDisplay(
                msg`Got an unexpected response — try again.`,
                "danger",
                "Retry",
            );
        case "External.AgentAuthMissing":
            return mkDisplay(
                msg`The assistant has no API key.`,
                "warning",
                "FixConfig",
                msg`Add your API key under Settings → Assistant, then restart the app.`,
            );
        case "External.AgentBusy":
            return mkDisplay(
                msg`The assistant is busy right now.`,
                "warning",
                "Retry",
                msg`Try again in a minute.`,
            );
        case "External.AgentUnreachable":
            return mkDisplay(
                msg`The assistant couldn't be reached.`,
                "danger",
                "Retry",
                msg`Try again in a moment.`,
            );
        case "External.AgentFailed":
            return mkDisplay(
                msg`The assistant couldn't answer.`,
                "danger",
                "Retry",
                msg`Try again. If it keeps happening, start a new conversation.`,
            );
        case "External.Unknown":
            return mkDisplay(
                msg`Something went wrong — try again.`,
                "danger",
                "Retry",
            );
        default:
            return exhaustSwitchCase(e);
    }
};
